<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { getFileInfo } from '@chatwoot/utils';

import FileIcon from 'next/icon/FileIcon.vue';
import Icon from 'next/icon/Icon.vue';

const { attachment } = defineProps({
  attachment: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();

// konta: inbound attachment blocked by the security whitelist (no stored file)
const isBlocked = computed(() => attachment.blocked);

const fileDetails = computed(() => {
  return getFileInfo(attachment?.dataUrl || '');
});

const displayFileName = computed(() => {
  const { base, type } = fileDetails.value;
  const truncatedName = (str, maxLength, hasExt) =>
    str.length > maxLength
      ? `${str.substring(0, maxLength).trimEnd()}${hasExt ? '..' : '...'}`
      : str;

  if (isBlocked.value) {
    return truncatedName(
      attachment.fallbackTitle || t('CONVERSATION.UNKNOWN_FILE_TYPE'),
      14,
      false
    );
  }

  return type
    ? `${truncatedName(base, 12, true)}.${type}`
    : truncatedName(base, 14, false);
});

const textColorClass = computed(() => {
  const colorMap = {
    '7z': 'dark:text-[#EDEEF0] text-[#2F265F]',
    csv: 'text-n-amber-12',
    doc: 'dark:text-[#D6E1FF] text-[#1F2D5C]', // indigo-12
    docx: 'dark:text-[#D6E1FF] text-[#1F2D5C]', // indigo-12
    json: 'text-n-slate-12',
    odt: 'dark:text-[#D6E1FF] text-[#1F2D5C]', // indigo-12
    pdf: 'text-n-slate-12',
    ppt: 'dark:text-[#FFE0C2] text-[#582D1D]',
    pptx: 'dark:text-[#FFE0C2] text-[#582D1D]',
    rar: 'dark:text-[#EDEEF0] text-[#2F265F]',
    rtf: 'dark:text-[#D6E1FF] text-[#1F2D5C]', // indigo-12
    tar: 'dark:text-[#EDEEF0] text-[#2F265F]',
    txt: 'text-n-slate-12',
    xls: 'text-n-teal-12',
    xlsx: 'text-n-teal-12',
    zip: 'dark:text-[#EDEEF0] text-[#2F265F]',
  };

  return colorMap[fileDetails.value.type] || 'text-n-slate-12';
});
</script>

<template>
  <div
    class="h-9 gap-2 overflow-hidden items-center flex px-2 rounded-lg border border-n-container"
    :class="isBlocked ? 'bg-n-ruby-3' : 'bg-n-alpha-white'"
  >
    <template v-if="isBlocked">
      <Icon icon="i-lucide-shield-alert" class="flex-shrink-0 text-n-ruby-11" />
      <span
        class="flex-1 min-w-0 text-sm max-w-36 text-n-ruby-11"
        :title="`${t('CONVERSATION.ATTACHMENT_BLOCKED')}: ${attachment.fallbackTitle || ''}`"
      >
        {{ displayFileName }}
      </span>
      <span
        v-tooltip="t('CONVERSATION.ATTACHMENT_BLOCKED')"
        class="flex-shrink-0 size-9 grid place-content-center text-n-ruby-11"
      >
        <Icon icon="i-lucide-ban" />
      </span>
    </template>
    <template v-else>
      <FileIcon class="flex-shrink-0" :file-type="fileDetails.type" />
      <span
        class="flex-1 min-w-0 text-sm max-w-36"
        :title="fileDetails.name"
        :class="textColorClass"
      >
        {{ displayFileName }}
      </span>
      <a
        v-tooltip="t('CONVERSATION.DOWNLOAD')"
        class="flex-shrink-0 size-9 grid place-content-center cursor-pointer text-n-slate-11 hover:text-n-slate-12 transition-colors"
        :href="attachment.dataUrl"
        rel="noreferrer noopener nofollow"
        target="_blank"
      >
        <Icon icon="i-lucide-download" />
      </a>
    </template>
  </div>
</template>
